import type { Layout } from '../data/types';
import type { LandHeight } from './types';
import type { Vec2 } from '../geo/types';
import { distancePointSegment, pointInRing, ringBounds } from '../geo/polygon';

/** Reconstructed grading around the Nieuwe Kerk, retaining its existing foundation datum.
 * The local apron meets the walls, then blends into the district over 45 m, to the Nieuwezijds
 * Voorburgwal's low quay on the west. These are modelling
 * levels, not a recovered 1538 elevation survey. */
export function fitChurchGround(layout: Layout, elevation: LandHeight): LandHeight {
  const church = layout.landmarks.find((l) => l.id === 'landmark-nieuwe-kerk');
  if (!church) return elevation;
  const ring = church.footprint;
  const height = Math.max(...ring.map(elevation));
  const bounds = ringBounds(ring);
  const apron = 8;
  const transition = 45;
  const reach = apron + transition;
  const edges = ring.map((a, i) => ({ a, b: ring[(i + 1) % ring.length]! }));
  const fitted = (p: Vec2) => {
    const base = elevation(p);
    if (
      p[0] < bounds.minX - reach ||
      p[0] > bounds.maxX + reach ||
      p[1] < bounds.minY - reach ||
      p[1] > bounds.maxY + reach
    )
      return base;
    if (pointInRing(p, ring)) return height;
    let distance = reach;
    for (const edge of edges) distance = Math.min(distance, distancePointSegment(p, edge));
    const t = Math.max(0, Math.min(1, (distance - apron) / transition));
    const weight = 1 - t * t * (3 - 2 * t);
    return base + (height - base) * weight;
  };
  const previous = elevation.elevationBounds;
  const elevationBounds = {
    minX: Math.min(previous?.minX ?? Infinity, bounds.minX - reach),
    minY: Math.min(previous?.minY ?? Infinity, bounds.minY - reach),
    maxX: Math.max(previous?.maxX ?? -Infinity, bounds.maxX + reach),
    maxY: Math.max(previous?.maxY ?? -Infinity, bounds.maxY + reach),
  };
  return Object.assign(fitted, { elevationBounds });
}
